import React, { useState, useEffect } from 'react';
import { AdjustmentsHorizontalIcon } from '@heroicons/react/24/outline';
import { useAuthStore } from '../stores/authStore';
import axios from 'axios';
import toast from 'react-hot-toast';
import EmailFiltersModal from '../components/EmailFiltersModal';
import {
  DEFAULT_EMAIL_FILTERS,
  EmailFilterState,
  countActiveEmailFilters,
} from '../utils/emailFilterUtils';

interface InboundEmail {
  id: string;
  messageId: string;
  fromEmail: string;
  fromName?: string;
  toEmail: string;
  subject: string;
  bodyText?: string;
  bodyHtml?: string;
  receivedAt: string;
  processedAt?: string;
  status: 'pending' | 'processed' | 'failed' | 'skipped';
  detectedDomain?: string;
  routingDetails?: {
    matchedRule?: string;
    domain?: string;
    clientOrganization?: { id: string; name: string };
    staffOrganization?: { id: string; name: string };
    contact?: { email: string; name?: string; userId?: string };
    ticket?: { id: string; number: string; title: string };
    routingReason?: string;
  };
  clientOrganization?: {
    id: string;
    name: string;
  };
  staffOrganization?: {
    id: string;
    name: string;
  };
  ticket?: {
    id: string;
    ticketNumber: string;
    title: string;
  };
  domainMapping?: {
    id: string;
    domain: string;
  };
}

interface Statistics {
  total: number;
  processed: number;
  failed: number;
  skipped: number;
  byStatus: Array<{ status: string; count: number }>;
  byClientOrg: Array<{ clientOrganizationId: string; count: number }>;
}

const EmailMonitoring: React.FC = () => {
  const { user } = useAuthStore();
  const [emails, setEmails] = useState<InboundEmail[]>([]);
  const [failedEmails, setFailedEmails] = useState<InboundEmail[]>([]);
  const [statistics, setStatistics] = useState<Statistics | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'all' | 'failed' | 'statistics'>('all');
  const [filters, setFilters] = useState<EmailFilterState>(DEFAULT_EMAIL_FILTERS);
  const [showFiltersModal, setShowFiltersModal] = useState(false);
  const [filterDraft, setFilterDraft] = useState<EmailFilterState>(DEFAULT_EMAIL_FILTERS);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedEmail, setSelectedEmail] = useState<InboundEmail | null>(null);
  const [clientOrganizations, setClientOrganizations] = useState<any[]>([]);
  const [assignForm, setAssignForm] = useState({
    clientOrganizationId: '',
    createTicket: false,
  });

  const activeFilterCount = countActiveEmailFilters(filters);

  const openFiltersModal = () => {
    setFilterDraft({ ...filters });
    setShowFiltersModal(true);
  };

  const applyFilters = () => {
    setFilters({ ...filterDraft });
    setShowFiltersModal(false);
  };

  const clearFilters = () => {
    setFilterDraft(DEFAULT_EMAIL_FILTERS);
    setFilters(DEFAULT_EMAIL_FILTERS);
    setShowFiltersModal(false);
  };

  const organizationOptions = clientOrganizations.map(org => ({
    id: org.id,
    label: org.name,
  }));

  useEffect(() => {
    loadData();
    loadClientOrganizations();
  }, [activeTab, filters]);

  const loadData = async () => {
    setIsLoading(true);
    try {
      if (activeTab === 'failed') {
        const response = await axios.get('/api/email-piping/inbound-emails/failed');
        setFailedEmails(response.data.data || []);
      } else if (activeTab === 'statistics') {
        const response = await axios.get('/api/email-piping/statistics');
        setStatistics(response.data.data);
      } else {
        const params = new URLSearchParams();
        if (filters.status) params.append('status', filters.status);
        if (filters.fromEmail) params.append('fromEmail', filters.fromEmail);
        if (filters.toEmail) params.append('toEmail', filters.toEmail);
        if (filters.clientOrgId) params.append('clientOrganizationId', filters.clientOrgId);
        params.append('limit', '100');
        params.append('page', '1');

        const response = await axios.get(`/api/email-piping/inbound-emails?${params}`);
        setEmails(response.data.data || []);
      }
    } catch (error: any) {
      console.error('Error loading data:', error);
      toast.error('Failed to load email data');
    } finally {
      setIsLoading(false);
    }
  };

  const loadClientOrganizations = async () => {
    try {
      const response = await axios.get('/api/organizations');
      setClientOrganizations(response.data.data || []);
    } catch (error) {
      console.error('Error loading client organizations:', error);
    }
  };

  const handleAssignEmail = async () => {
    if (!selectedEmail || !assignForm.clientOrganizationId) {
      toast.error('Please select a client organization');
      return;
    }

    try {
      const response = await axios.post(
        `/api/email-piping/inbound-emails/${selectedEmail.id}/assign`,
        assignForm
      );

      if (response.data.success) {
        toast.success(
          assignForm.createTicket && response.data.ticketCreated
            ? `Email assigned and ticket ${response.data.ticketNumber} created!`
            : 'Email assigned to client organization'
        );
        setShowAssignModal(false);
        setSelectedEmail(null);
        setAssignForm({ clientOrganizationId: '', createTicket: false });
        loadData();
      }
    } catch (error: any) {
      console.error('Error assigning email:', error);
      toast.error(error.response?.data?.error || 'Failed to assign email');
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'processed':
        return 'bg-green-100 text-green-800';
      case 'failed':
        return 'bg-red-100 text-red-800';
      case 'skipped':
        return 'bg-yellow-100 text-yellow-800';
      case 'pending':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <div className="sm:flex sm:items-center sm:justify-between mb-6">
        <div>
          <h1 className="text-3xl font-semibold text-gray-900">Email Monitoring</h1>
          <p className="mt-2 text-sm text-gray-700">
            Monitor inbound emails, routing, and ticket creation
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="mb-6">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('all')}
              className={`${
                activeTab === 'all'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
            >
              All Emails
            </button>
            <button
              onClick={() => setActiveTab('failed')}
              className={`${
                activeTab === 'failed'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
            >
              Failed Emails
              {failedEmails.length > 0 && (
                <span className="ml-2 bg-red-100 text-red-800 text-xs font-medium px-2 py-1 rounded-full">
                  {failedEmails.length}
                </span>
              )}
            </button>
            <button
              onClick={() => setActiveTab('statistics')}
              className={`${
                activeTab === 'statistics'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
            >
              Statistics
            </button>
          </nav>
        </div>
      </div>

      {/* Toolbar (All Emails tab) */}
      {activeTab === 'all' && (
        <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={openFiltersModal}
              className="inline-flex items-center justify-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
            >
              <AdjustmentsHorizontalIcon className="h-4 w-4" />
              Filters
              {activeFilterCount > 0 && (
                <span className="inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-violet-600 px-1.5 text-xs font-semibold text-white">
                  {activeFilterCount}
                </span>
              )}
            </button>
            {activeFilterCount > 0 && (
              <button
                type="button"
                onClick={clearFilters}
                className="text-sm text-gray-600 hover:text-gray-900"
              >
                Clear all
              </button>
            )}
          </div>
          <p className="text-sm text-gray-500">
            <span className="font-medium text-gray-900">{emails.length}</span>
            {' email'}
            {emails.length === 1 ? '' : 's'}
            {activeFilterCount > 0 ? ' matching filters' : ''}
          </p>
        </div>
      )}

      <EmailFiltersModal
        isOpen={showFiltersModal}
        draft={filterDraft}
        onChange={setFilterDraft}
        onClose={() => setShowFiltersModal(false)}
        onApply={applyFilters}
        onClearAll={() => setFilterDraft(DEFAULT_EMAIL_FILTERS)}
        matchCount={emails.length}
        organizationOptions={organizationOptions}
      />

      {/* Content */}
      {isLoading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      ) : activeTab === 'statistics' ? (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
          <div className="card p-6">
            <h3 className="text-sm font-medium text-gray-500">Total Emails</h3>
            <p className="mt-2 text-3xl font-semibold text-gray-900">{statistics?.total || 0}</p>
          </div>
          <div className="card p-6">
            <h3 className="text-sm font-medium text-gray-500">Processed</h3>
            <p className="mt-2 text-3xl font-semibold text-green-600">{statistics?.processed || 0}</p>
          </div>
          <div className="card p-6">
            <h3 className="text-sm font-medium text-gray-500">Failed</h3>
            <p className="mt-2 text-3xl font-semibold text-red-600">{statistics?.failed || 0}</p>
          </div>
          <div className="card p-6">
            <h3 className="text-sm font-medium text-gray-500">Skipped</h3>
            <p className="mt-2 text-3xl font-semibold text-yellow-600">{statistics?.skipped || 0}</p>
          </div>
        </div>
      ) : (
        <div className="card">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    From
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    To
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Subject
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Client Org
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Staff Org
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Ticket
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Received
                  </th>
                  {activeTab === 'failed' && (
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {(activeTab === 'failed' ? failedEmails : emails).map((email) => (
                  <tr key={email.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{email.fromEmail}</div>
                      {email.fromName && (
                        <div className="text-sm text-gray-500">{email.fromName}</div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {email.toEmail}
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900 max-w-xs truncate" title={email.subject}>
                        {email.subject || '(No subject)'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(
                          email.status
                        )}`}
                      >
                        {email.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {email.clientOrganization?.name || email.routingDetails?.clientOrganization?.name || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {email.staffOrganization?.name || email.routingDetails?.staffOrganization?.name || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {email.ticket ? (
                        <a
                          href={`/tickets/${email.ticket.id}`}
                          className="text-primary-600 hover:text-primary-800"
                        >
                          {email.ticket.ticketNumber}
                        </a>
                      ) : email.routingDetails?.ticket ? (
                        <a
                          href={`/tickets/${email.routingDetails.ticket.id}`}
                          className="text-primary-600 hover:text-primary-800"
                        >
                          {email.routingDetails.ticket.number}
                        </a>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(email.receivedAt)}
                    </td>
                    {activeTab === 'failed' && (
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <button
                          onClick={() => {
                            setSelectedEmail(email);
                            setShowAssignModal(true);
                          }}
                          className="text-primary-600 hover:text-primary-800 font-medium"
                        >
                          Assign
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
            {(activeTab === 'failed' ? failedEmails : emails).length === 0 && (
              <div className="text-center py-12 text-gray-500">
                No emails found
              </div>
            )}
          </div>
        </div>
      )}

      {/* Assign Modal */}
      {showAssignModal && selectedEmail && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Assign Email to Client</h3>
            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-2">
                <strong>From:</strong> {selectedEmail.fromEmail}
              </p>
              <p className="text-sm text-gray-600 mb-2">
                <strong>Subject:</strong> {selectedEmail.subject || '(No subject)'}
              </p>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Client Organization *
              </label>
              <select
                value={assignForm.clientOrganizationId}
                onChange={(e) =>
                  setAssignForm({ ...assignForm, clientOrganizationId: e.target.value })
                }
                className="input-field w-full"
                required
              >
                <option value="">Select Client Organization</option>
                {clientOrganizations.map((org) => (
                  <option key={org.id} value={org.id}>
                    {org.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="mb-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={assignForm.createTicket}
                  onChange={(e) =>
                    setAssignForm({ ...assignForm, createTicket: e.target.checked })
                  }
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <span className="ml-2 text-sm text-gray-700">Create ticket immediately</span>
              </label>
            </div>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowAssignModal(false);
                  setSelectedEmail(null);
                  setAssignForm({ clientOrganizationId: '', createTicket: false });
                }}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button onClick={handleAssignEmail} className="btn-primary">
                Assign
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmailMonitoring;

